import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import Segmented from '../../components/ui/Segmented';
import Toggle from '../../components/ui/Toggle';
import { Field, Select, TextInput, Textarea } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  createBlog,
  getAuthorList,
  getCategoryList,
  getSelectedBlogDetails,
  updateBlog,
} from '../../actions/blogAction';
import './growth.css';

const VIEW_MODES = [
  { value: 'md', label: 'Markdown', icon: 'bx-code-alt' },
  { value: 'split', label: 'Split', icon: 'bx-columns' },
  { value: 'preview', label: 'Preview', icon: 'bx-show' },
];

const META_DESC_LIMIT = 160;

const EMPTY = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  author_id: '',
  category_id: '',
  is_active: true,
  is_featured: false,
  reading_time: '',
  seo_title: '',
  seo_description: '',
  meta_title: '',
};

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

/** Tag chips with an inline input, per the handoff's Tags rail card. */
function TagInput({ label, tags, onChange }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const value = draft.trim();
    if (value && !tags.includes(value)) onChange([...tags, value]);
    setDraft('');
  };
  return (
    <Field label={label}>
      {tags.length > 0 && (
        <div className="sv-tag-list">
          {tags.map((tag) => (
            <span className="sv-tag" key={tag}>
              {tag}
              <button type="button" onClick={() => onChange(tags.filter((t) => t !== tag))} aria-label={`Remove ${tag}`}>
                <i className="bx bx-x" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
      <TextInput
        value={draft}
        placeholder="Type and press Enter"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
    </Field>
  );
}

export default function BlogEditor() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, toast } = useOverlays();

  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [tags, setTags] = useState([]);
  const [metaTags, setMetaTags] = useState([]);
  const [image, setImage] = useState(null);
  const [viewMode, setViewMode] = useState('split');
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    getAuthorList()
      .then((list) => setAuthors((list || []).map((a) => ({ value: a.id, label: a.name }))))
      .catch(() => setAuthors([]));
    getCategoryList()
      .then((list) => setCategories((list || []).map((c) => ({ value: c.id, label: c.name }))))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    getSelectedBlogDetails(dispatch, id)
      .then((res) => {
        const post = res?.items?.[0] || res;
        if (!post) return;
        setForm({
          title: post.title || '',
          slug: post.slug || '',
          excerpt: post.excerpt || '',
          content: post.content || '',
          author_id: post.author?.id || '',
          category_id: post.category?.id || '',
          is_active: !!post.is_active,
          is_featured: !!post.is_featured,
          reading_time: post.reading_time || '',
          seo_title: post.seo_title || '',
          seo_description: post.seo_description || '',
          meta_title: post.meta_title || '',
        });
        setTags(post.tags || []);
        setMetaTags(post.meta_tags || []);
        setSlugTouched(true);
      })
      .catch(() => toast('Could not load the post', { tone: 'error' }));
  }, [id, isNew, dispatch, toast]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onTitleChange = (e) => {
    const title = e.target.value;
    setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }));
  };

  const rendered = useMemo(() => {
    try {
      return marked.parse(form.content || '', { breaks: true });
    } catch {
      return '<p>Preview unavailable.</p>';
    }
  }, [form.content]);

  const valid = form.title.trim() && form.slug.trim() && form.content.trim() && form.author_id && form.category_id;

  const save = useCallback(
    async (publish) => {
      if (!valid) return;
      if (publish) {
        const ok = await confirm({
          title: isNew ? 'Publish this post?' : 'Update the published post?',
          body: `“${form.title}” will be live at /blog/${form.slug} immediately.`,
          confirmLabel: publish ? 'Publish' : 'Update',
        });
        if (!ok) return;
      }

      setBusy(true);
      const data = new FormData();
      Object.entries({
        ...form,
        is_active: publish ? true : form.is_active,
        reading_time: form.reading_time || '',
      }).forEach(([k, v]) => data.append(k, v ?? ''));
      data.append('tags', JSON.stringify(tags));
      data.append('meta_tags', JSON.stringify(metaTags));
      if (image) data.append('featured_image', image);

      try {
        if (isNew) await createBlog(data, dispatch);
        else await updateBlog(id, data, dispatch);
        navigate('/blog');
      } catch {
        /* the action already toasted the failure */
      } finally {
        setBusy(false);
      }
    },
    [valid, confirm, isNew, form, tags, metaTags, image, dispatch, id, navigate]
  );

  const descOver = (form.seo_description || '').length > META_DESC_LIMIT;

  return (
    <>
      <PageHeader
        section="Growth"
        title={isNew ? 'New post' : 'Edit post'}
        back="blog"
        backTo="/blog"
      />

      <div className="sv-screen-body">
        <div className="sv-editor">
          <div>
            <div className="sv-card" style={{ padding: 18, marginBottom: 14 }}>
              <input
                className="sv-editor__title-input"
                placeholder="Post title"
                value={form.title}
                onChange={onTitleChange}
              />
              <div className="sv-editor__slug">
                <span className="sv-editor__slug-prefix">/blog/</span>
                <input
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm((f) => ({ ...f, slug: e.target.value }));
                  }}
                  placeholder="post-slug"
                  aria-label="Slug"
                />
              </div>
              <div style={{ marginTop: 14 }}>
                <Field label="Excerpt">
                  <Textarea rows={3} value={form.excerpt} onChange={set('excerpt')} />
                </Field>
              </div>
            </div>

            <div className="sv-card">
              <div className="sv-editor__toolbar">
                <span className="sv-card__title">Content</span>
                <Segmented
                  options={VIEW_MODES}
                  value={viewMode}
                  onChange={setViewMode}
                  label="Editor view"
                />
              </div>
              <div className="sv-editor__panes">
                {(viewMode === 'md' || viewMode === 'split') && (
                  <div className="sv-editor__pane">
                    <textarea
                      className="sv-editor__raw"
                      value={form.content}
                      onChange={set('content')}
                      placeholder={'# Heading\n\nWrite the post in Markdown…'}
                      spellCheck
                    />
                  </div>
                )}
                {(viewMode === 'preview' || viewMode === 'split') && (
                  <div className="sv-editor__pane">
                    {/* Content is authored by admins in Markdown; marked renders it for
                        preview only. Treat any future user-authored source as untrusted
                        and sanitise before doing this. */}
                    <div
                      className="sv-editor__preview"
                      dangerouslySetInnerHTML={{ __html: rendered }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="sv-editor__rail">
            <section className="sv-card sv-rail-card">
              <h3 className="sv-rail-card__title">Publish</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Field label="Author" required>
                  <Select options={authors} placeholder="Choose" value={form.author_id} onChange={set('author_id')} />
                </Field>
                <Field label="Category" required>
                  <Select options={categories} placeholder="Choose" value={form.category_id} onChange={set('category_id')} />
                </Field>
                <Field label="Reading time" hint="Minutes">
                  <TextInput type="number" value={form.reading_time} onChange={set('reading_time')} />
                </Field>
                <div className="sv-rail-toggle">
                  <span>Active</span>
                  <Toggle checked={form.is_active} onChange={(v) => setForm((f) => ({ ...f, is_active: v }))} label="Active" />
                </div>
                <div className="sv-rail-toggle">
                  <span>Featured</span>
                  <Toggle checked={form.is_featured} onChange={(v) => setForm((f) => ({ ...f, is_featured: v }))} label="Featured" />
                </div>
                <Field label="Featured image">
                  <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <Button variant="secondary" onClick={() => save(false)} loading={busy} disabled={!valid}>
                  Save draft
                </Button>
                <Button variant="primary" onClick={() => save(true)} loading={busy} disabled={!valid}>
                  Publish
                </Button>
              </div>
            </section>

            <section className="sv-card sv-rail-card">
              <h3 className="sv-rail-card__title">Tags</h3>
              <TagInput label="Tags" tags={tags} onChange={setTags} />
            </section>

            <section className="sv-card sv-rail-card">
              <h3 className="sv-rail-card__title">SEO</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Field label="Meta title">
                  <TextInput value={form.meta_title} onChange={set('meta_title')} />
                </Field>
                <Field label="SEO title">
                  <TextInput value={form.seo_title} onChange={set('seo_title')} />
                </Field>
                <div>
                  <Field label="Meta description">
                    <Textarea rows={3} value={form.seo_description} onChange={set('seo_description')} invalid={descOver} />
                  </Field>
                  <div className={`sv-char-count ${descOver ? 'is-over' : ''}`}>
                    {(form.seo_description || '').length} / {META_DESC_LIMIT}
                  </div>
                </div>
                <TagInput label="Meta keywords" tags={metaTags} onChange={setMetaTags} />
              </div>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}
